import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Edit, Trash2, UserPlus, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import {
  DataTable,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SearchInput,
  FilterDropdown,
  Pagination,
  Card,
  CardContent,
  ErrorDisplay,
  EmptyState,
} from '../../components/ui';
import { VirtualizedTable, useVirtualization } from '../../components/ui/virtualized-table';
import { UserTableSkeleton } from '../../components/ui/table-skeleton';
import { usePermissions } from '../../shared/lib/hooks/use-auth';
import { useUsers } from '../../shared/lib/hooks/use-users';
import { cacheUtils } from '../../shared/lib/query-client';
import type { User, UserSearchParams, Column } from '../../shared/types';

interface UserTableProps {
  onCreateUser?: () => void;
  onEditUser?: (user: User) => void;
  onDeleteUser?: (user: User) => void;
  onAssignRole?: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  onCreateUser,
  onEditUser,
  onDeleteUser,
  onAssignRole,
}) => {
  const { t, i18n } = useTranslation();
  const { canWriteUsers, canDeleteUsers, canManageUsers } = usePermissions();

  // Search and filter state
  const [searchParams, setSearchParams] = React.useState<UserSearchParams>({
    page: 1,
    size: 10,
    search: '',
    isActive: undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Fetch users data
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useUsers(searchParams);

  // Determine if we should use virtualization
  const { shouldVirtualize } = useVirtualization(usersData?.items?.length || 0, 50);

  // Prefetch user details on hover for better UX
  const handleUserHover = React.useCallback((user: User) => {
    cacheUtils.prefetchUser(user.id);
  }, []);

  // Get locale for date formatting
  const dateLocale = i18n.language === 'ru' ? ru : enUS;

  // Handle search
  const handleSearch = (search: string) => {
    setSearchParams(prev => ({
      ...prev,
      search,
      page: 1, // Reset to first page when searching
    }));
  };

  // Handle filter change
  const handleFilterChange = (value: string | boolean | null) => {
    const isActive = typeof value === 'boolean' ? value : null;
    setSearchParams(prev => ({
      ...prev,
      isActive: isActive === null ? undefined : isActive,
      page: 1, // Reset to first page when filtering
    }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setSearchParams(prev => ({
      ...prev,
      size,
      page: 1, // Reset to first page when changing page size
    }));
  };

  // Format user display name
  const formatUserName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  // Format roles display
  const formatRoles = (roles: User['roles']) => {
    if (!roles || roles.length === 0) {
      return <span className="text-muted-foreground">{t('users.noRoles', 'No roles')}</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {roles.slice(0, 2).map((role) => (
          <span
            key={role.id}
            className="inline-flex items-center rounded-full bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground"
          >
            {role.name}
          </span>
        ))}
        {roles.length > 2 && (
          <span className="text-xs text-muted-foreground">
            +{roles.length - 2} more
          </span>
        )}
      </div>
    );
  };

  // Table columns definition
  const columns: Column<User>[] = [
    {
      key: 'email',
      title: t('auth.email'),
      render: (_, user) => (
        <div className="flex flex-col">
          <span className="font-medium">{user.email}</span>
          {(user.firstName || user.lastName) && (
            <span className="text-sm text-muted-foreground">
              {formatUserName(user)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'isActive',
      title: t('common.status'),
      render: (_, user) => (
        <div className="flex items-center gap-2">
          {user.isActive ? (
            <>
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-sm">{t('common.active')}</span>
            </>
          ) : (
            <>
              <div className="h-2 w-2 rounded-full bg-gray-400" />
              <span className="text-sm">{t('common.inactive')}</span>
            </>
          )}
          {user.isSuperuser && (
            <Shield className="h-4 w-4 text-amber-500" />
          )}
        </div>
      ),
    },
    {
      key: 'roles',
      title: t('users.roles'),
      render: (_, user) => formatRoles(user.roles),
    },
    {
      key: 'lastLoginAt',
      title: t('users.lastLogin'),
      render: (_, user) => {
        if (!user.lastLoginAt) {
          return <span className="text-muted-foreground">Never</span>;
        }
        return (
          <span className="text-sm">
            {format(new Date(user.lastLoginAt), 'PPp', { locale: dateLocale })}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      title: t('common.createdAt'),
      render: (_, user) => (
        <span className="text-sm">
          {format(new Date(user.createdAt), 'PP', { locale: dateLocale })}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('common.actions'),
      render: (_, user) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canWriteUsers && (
              <DropdownMenuItem onClick={() => onEditUser?.(user)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
            )}
            {canManageUsers && (
              <DropdownMenuItem onClick={() => onAssignRole?.(user)}>
                <UserPlus className="mr-2 h-4 w-4" />
                {t('users.assignRole')}
              </DropdownMenuItem>
            )}
            {canDeleteUsers && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDeleteUser?.(user)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('common.delete')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      width: 50,
      align: 'center',
    },
  ];

  // Filter options
  const statusFilterOptions = [
    { label: t('common.all', 'All'), value: null },
    { label: t('common.active'), value: true },
    { label: t('common.inactive'), value: false },
  ];

  // Mobile card component for users
  const UserCard: React.FC<{ 
    user: User; 
    onMouseEnter?: () => void;
  }> = ({ user, onMouseEnter }) => (
    <Card className="w-full" onMouseEnter={onMouseEnter}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            {/* User info */}
            <div>
              <div className="font-medium text-sm">{user.email}</div>
              {(user.firstName || user.lastName) && (
                <div className="text-sm text-muted-foreground">
                  {formatUserName(user)}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              {user.isActive ? (
                <>
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="text-xs">{t('common.active')}</span>
                </>
              ) : (
                <>
                  <div className="h-2 w-2 rounded-full bg-gray-400" />
                  <span className="text-xs">{t('common.inactive')}</span>
                </>
              )}
              {user.isSuperuser && (
                <Shield className="h-3 w-3 text-amber-500" />
              )}
            </div>

            {/* Roles */}
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">{t('users.roles')}:</div>
              {formatRoles(user.roles)}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-1 text-xs text-muted-foreground">
              <div>
                {t('users.lastLogin')}: {' '}
                {user.lastLoginAt 
                  ? format(new Date(user.lastLoginAt), 'PP', { locale: dateLocale })
                  : 'Never'
                }
              </div>
              <div>
                {t('common.createdAt')}: {format(new Date(user.createdAt), 'PP', { locale: dateLocale })}
              </div>
            </div>
          </div>

          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canWriteUsers && (
                <DropdownMenuItem onClick={() => onEditUser?.(user)}>
                  <Edit className="mr-2 h-4 w-4" />
                  {t('common.edit')}
                </DropdownMenuItem>
              )}
              {canManageUsers && (
                <DropdownMenuItem onClick={() => onAssignRole?.(user)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t('users.assignRole')}
                </DropdownMenuItem>
              )}
              {canDeleteUsers && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDeleteUser?.(user)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('common.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {/* Header with search and filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
          <SearchInput
            placeholder={t('users.searchPlaceholder')}
            value={searchParams.search || ''}
            onChange={handleSearch}
            className="w-full sm:w-[300px]"
          />
          <FilterDropdown
            options={statusFilterOptions}
            value={searchParams.isActive}
            onValueChange={handleFilterChange}
            placeholder={t('common.status')}
          />
        </div>
        {canWriteUsers && (
          <Button onClick={onCreateUser} className="w-full sm:w-auto">
            <UserPlus className="mr-2 h-4 w-4" />
            {t('users.createUser')}
          </Button>
        )}
      </div>

      {/* Loading state with skeleton */}
      {isLoading && (
        <UserTableSkeleton 
          rows={searchParams.size || 10} 
          isMobile={false}
        />
      )}

      {/* Fetching indicator for background updates */}
      {isFetching && !isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" />
        </div>
      )}

      {/* Error state */}
      {!!error && (
        <ErrorDisplay
          title="Failed to load users"
          error={error as unknown as Error}
          onRetry={refetch}
        />
      )}

      {/* Empty state */}
      {!isLoading && !error && (!usersData?.items || usersData.items.length === 0) && (
        <EmptyState
          title={t('users.noUsersFound')}
          description={searchParams.search
            ? t('users.noUsersFoundSearch', 'Try adjusting your search criteria')
            : t('users.noUsersFoundEmpty', 'No users have been created yet')}
          action={canWriteUsers
            ? {
                label: t('users.createUser'),
                onClick: () => onCreateUser?.(),
              }
            : undefined}
        />
      )}

      {/* Data display - responsive with virtualization */}
      {!isLoading && !error && usersData?.items && usersData.items.length > 0 && (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block relative">
            {shouldVirtualize ? (
              <VirtualizedTable
                data={usersData.items}
                columns={columns}
                height={600}
                itemHeight={60}
                onRowClick={(user) => handleUserHover(user)}
              />
            ) : (
              <DataTable
                columns={columns}
                data={usersData.items}
                loading={false}
                error={undefined}
                onRetry={refetch}
              />
            )}
          </div>

          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {usersData.items.map((user: User) => (
              <UserCard 
                key={user.id} 
                user={user}
                onMouseEnter={() => handleUserHover(user)}
              />
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {usersData && usersData.total > 0 && (
        <Pagination
          currentPage={searchParams.page || 1}
          totalPages={usersData.pages}
          onPageChange={handlePageChange}
          showSizeChanger
          pageSize={searchParams.size || 10}
          onPageSizeChange={handlePageSizeChange}
          total={usersData.total}
        />
      )}
    </div>
  );
};